import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { MegaMenu } from './MegaMenu';

export const Header = ({ onOpenPopup, isLandingPage = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    {
      name: 'Services',
      path: '/services',
      submenu: [
        { name: 'Trademark Registration', path: '/services/trademark-registration' },
        { name: 'Copyright Registration', path: '/services/copyright-registration' },
        { name: 'Company Formation', path: '/services/company-formation' },
        { name: 'Trademark Search', path: '/services/trademark-search' },
        { name: 'Amazon Brand Registry', path: '/services/amazon-brand-registry' }
      ]
    },
    { name: 'Contact Us', path: '/contact' }
  ];

  if (isLandingPage) {
    return (
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-white'
      }`}>
        <div className="container mx-auto px-6 md:px-8 lg:px-12 max-w-7xl">
          <div className="flex items-center justify-between h-20">
            <Link to="/lp" className="flex items-center">
              <img src="/assets/img/brand/logo-dark.png" alt="Trademark Infinite" className="h-12 w-auto" />
            </Link>
            
            {/* Desktop Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Button onClick={onOpenPopup} className="bg-primary hover:bg-primary/90">
                Register My Trademark
              </Button>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/10" onClick={onOpenPopup}>
                Chat Now
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-700 hover:text-primary transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-3">
                <Button onClick={() => { onOpenPopup(); setIsMobileMenuOpen(false); }} className="w-full bg-primary hover:bg-primary/90">
                  Register My Trademark
                </Button>
                <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/10" onClick={() => { onOpenPopup(); setIsMobileMenuOpen(false); }}>
                  Chat Now
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>
    );
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-white'
    }`}>
      <div className="container mx-auto px-6 md:px-8 lg:px-12 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img src="/assets/img/brand/logo-dark.png" alt="Trademark Infinite" className="h-12 w-auto" />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.name === 'Services') {
                return (
                  <div key={link.path} className="relative">
                    <button
                      onMouseEnter={() => setIsMegaMenuOpen(true)}
                      onMouseLeave={() => setIsMegaMenuOpen(false)}
                      className="flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary text-gray-700"
                    >
                      {link.name}
                      <ChevronDown size={16} className={`transition-transform ${isMegaMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div 
                      onMouseEnter={() => setIsMegaMenuOpen(true)}
                      onMouseLeave={() => setIsMegaMenuOpen(false)}
                    >
                      <MegaMenu isOpen={isMegaMenuOpen} onClose={() => setIsMegaMenuOpen(false)} onOpenPopup={onOpenPopup} />
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={link.path}>
                  <Link
                    to={link.path}
                    className={`text-sm font-medium transition-colors hover:text-primary ${
                      location.pathname === link.path ? 'text-primary' : 'text-gray-700'
                    }`}
                  >
                    {link.name}
                  </Link>
                </div>
              );
            })}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <Button onClick={onOpenPopup} className="bg-primary hover:bg-primary/90">
              Get Started
            </Button>
            <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
              Chat Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-primary transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <div key={link.path}>
                  <Link
                    to={link.path}
                    className="block text-sm font-medium text-gray-700 hover:text-primary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                  {link.submenu && (
                    <div className="ml-4 mt-2 flex flex-col gap-2">
                      {link.submenu.map((sublink) => (
                        <Link
                          key={sublink.path}
                          to={sublink.path}
                          className="block text-sm text-gray-600 hover:text-primary transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {sublink.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex flex-col gap-2 mt-4">
                <Button onClick={() => { onOpenPopup(); setIsMobileMenuOpen(false); }} className="bg-primary hover:bg-primary/90 w-full">
                  Get Started
                </Button>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary/10 w-full">
                  Chat Now
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};